import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import omit from 'lodash/omit'
import { DocumentRequestPayload, Facility } from 'apps/marketplace/apiTypes/facilities'
import { TFacilityScheduleSchema } from '../components/FacilitySchedule/FacilityScheduleForm'

/**
 * Represent the matrix of possible rules
 */
export enum RuleType {
  LivePickup = 'livePickup',
  DropPickup = 'dropPickup',
  LiveDelivery = 'liveDelivery',
  DropDelivery = 'dropDelivery',
}

export type TUnloadType = 'LIVE' | 'DROP' | null
export type TStopType = 'PICKUP' | 'DELIVERY' | null
export type TAppointmentType = 'FCFS' | 'APPOINTMENT'
export type TProcedureRequirement = 'APPROVAL' | 'NOTIFICATION'
export type TSchedulingMethod = 'CONTACT' | 'TMS'

export type TFacilitySchedule = Required<Pick<Facility, 'schedule'>>['schedule']

// We are taking the TFacilitySchedule directly from facility, but there the documents type is
// DocumentResponsePayload with a mandatory 'id'. So here we are replace the documents with the
// correct DocumentRequestPayload type to send update calls
export type TFacilityScheduleRequest = Omit<TFacilitySchedule, 'documents'> & {
  documents: DocumentRequestPayload[] | undefined
}
// Gets the "rules" array prop, and looks at only a single item in the array
export type TFacilityScheduleRule = Required<TFacilitySchedule>['rules'][number]

// Produces the same interface output as in your PR
export interface IScheduleRule extends TFacilityScheduleRule {}

const NO_RULES = 0
const ALL_RULES_EQUALS = 1
const ALL_RULES_DIFFERENT = 4

const EMPTY_RULE: Readonly<IScheduleRule> = {}

const getRuleForStopAndUnloadType = (
  rules: IScheduleRule[],
  stopType: NonNullable<TStopType>,
  unloadType: NonNullable<TUnloadType>
): IScheduleRule => {
  if (!rules || rules.length === NO_RULES) {
    return { ...EMPTY_RULE, stopType, unloadType }
  }

  if (rules.length === ALL_RULES_EQUALS) {
    return { ...rules[0], stopType, unloadType }
  }

  if (rules.length === ALL_RULES_DIFFERENT) {
    const rule = rules.find(r => r.stopType === stopType && r.unloadType === unloadType)
    if (!rule) {
      throw new Error(`Rule not found!`)
    }
    return rule
  }

  // Either live/drop or pickup/delivery are different
  // We can return the first that match stopType or unloadType
  // as the backend will return 2 rules with either stopType or unloadType in it.
  const ruleModel = rules.find(r => r.stopType === stopType || r.unloadType === unloadType)
  return { ...ruleModel, stopType, unloadType }
}

export type TRules = Record<RuleType, IScheduleRule>

/**
 * Convert the rules array from the API to a Record of 4 rules for each
 * StopType/UnloadType combination for easier access.
 *
 * @param data Rules from the API
 * @returns TRules, really just a helper to easily access rules by SchedulingRuleType
 */
export const expandScheduleRules = (data: IScheduleRule[]): TRules => ({
  [RuleType.LivePickup]: getRuleForStopAndUnloadType(data, 'PICKUP', 'LIVE'),
  [RuleType.LiveDelivery]: getRuleForStopAndUnloadType(data, 'DELIVERY', 'LIVE'),
  [RuleType.DropPickup]: getRuleForStopAndUnloadType(data, 'PICKUP', 'DROP'),
  [RuleType.DropDelivery]: getRuleForStopAndUnloadType(data, 'DELIVERY', 'DROP'),
})

const removeUnloadAndStopType = (rule: IScheduleRule) => {
  return omit(rule, ['stopType', 'unloadType'])
}

export const isUnloadTypeRulesDifferent = (rules: TRules): boolean => {
  const livePickup = removeUnloadAndStopType({ ...rules.livePickup })
  const liveDelivery = removeUnloadAndStopType({ ...rules.liveDelivery })
  const dropPickup = removeUnloadAndStopType({ ...rules.dropPickup })
  const dropDelivery = removeUnloadAndStopType({ ...rules.dropDelivery })
  return !isEqual(livePickup, dropPickup) || !isEqual(liveDelivery, dropDelivery)
}

export const isStopTypeRulesDifferent = (rules: TRules): boolean => {
  const livePickup = removeUnloadAndStopType({ ...rules.livePickup })
  const liveDelivery = removeUnloadAndStopType({ ...rules.liveDelivery })
  const dropPickup = removeUnloadAndStopType({ ...rules.dropPickup })
  const dropDelivery = removeUnloadAndStopType({ ...rules.dropDelivery })
  return !isEqual(livePickup, liveDelivery) || !isEqual(dropPickup, dropDelivery)
}

const buildRule = (view: TFacilityScheduleSchema, ruleType: RuleType): IScheduleRule => {
  let stopType: TStopType
  let unloadType: TUnloadType

  if (ruleType === RuleType.LivePickup || ruleType === RuleType.LiveDelivery) {
    unloadType = 'LIVE'
  } else {
    unloadType = 'DROP'
  }

  if (ruleType === RuleType.LivePickup || ruleType === RuleType.DropPickup) {
    stopType = 'PICKUP'
  } else {
    stopType = 'DELIVERY'
  }

  return {
    stopType,
    unloadType,
    appointmentType: get(view, `${ruleType}AppointmentType`) as TAppointmentType,
    autoScheduling: get(view, `${ruleType}AutoScheduling`),
    procedureRequirements: get(view, `${ruleType}ProcedureRequirement`) as TProcedureRequirement,
    schedulingMethod: get(view, `${ruleType}SchedulingMethod`) as TSchedulingMethod,
    tmsNotes: get(view, `${ruleType}TMSSchedulingNotes`),
    tmsUrl: get(view, `${ruleType}TMSSchedulingUrl`),
    workInsAllowed: get(view, `${ruleType}WorksIns`),
    quickRequest: get(view, `${ruleType}QuickRequest`),
  }
}

const mapOmit = <K extends keyof IScheduleRule>(
  rules: IScheduleRule[],
  props: K[]
): Omit<IScheduleRule, K>[] => {
  return rules.map(rule => omit(rule, props))
}

const removeEmptyProps = (rule: IScheduleRule) => {
  return Object.fromEntries(Object.entries(rule).filter(([, v]) => !isNull(v) && !isUndefined(v)))
}

export const schedulingRulesFromViewToAPI = (
  viewData: TFacilityScheduleSchema,
  original: IScheduleRule[] | undefined
): IScheduleRule[] | null => {
  const pickupLive = buildRule(viewData, RuleType.LivePickup)
  const deliveryLive = buildRule(viewData, RuleType.LiveDelivery)
  const pickupDrop = buildRule(viewData, RuleType.DropPickup)
  const deliveryDrop = buildRule(viewData, RuleType.DropDelivery)

  const { stopTypeDiffer, unloadTypeDiffer } = viewData || {}
  let result: IScheduleRule[] | null = null
  if (stopTypeDiffer && unloadTypeDiffer) {
    result = [pickupLive, deliveryLive, pickupDrop, deliveryDrop]
  } else if (stopTypeDiffer) {
    result = mapOmit([pickupLive, deliveryLive], ['unloadType'])
  } else if (unloadTypeDiffer) {
    result = mapOmit([pickupLive, pickupDrop], ['stopType'])
  } else if (!stopTypeDiffer && !unloadTypeDiffer) {
    result = mapOmit([pickupLive], ['stopType', 'unloadType'])
  }

  const cleanOriginal = original?.map(removeEmptyProps)
  const cleanNew = result?.map(removeEmptyProps)
  if (isEqual(cleanOriginal, cleanNew)) {
    return null
  }
  return result
}
